import React, {useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import StyledTaskAddDialog from 'components/organisms/Dialogs/TaskAddDialog/TaskAddDialog.styles';
import {Span} from 'components/atoms/Text/Typography/Typography';
import DialogHeader from 'components/molecules/Dialogs/DialogHeader/DialogHeader';
import TaskAddWizard from 'components/organisms/Wizards/TaskAddWizard/TaskAddWizard';
import utils from 'helpers/utils';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ActionButton from 'components/molecules/Buttons/ActionButton/ActionButton';
import DialogContent from 'components/atoms/Dialogs/DialogContent/DialogContent';
import DialogFooter from 'components/molecules/Dialogs/DialogFooter/DialogFooter';
import Add from '@mui/icons-material/Add';
import {useDialogControl} from 'components/organisms/Providers/DialogProvider/DialogProvider';
import ConfirmDialog from 'components/organisms/Dialogs/ConfirmDialog/ConfirmDialog';

const TaskAddDialog = React.forwardRef((props, ref) => {
  const {
    count,
    title,
    subtitle,
    onClose,
    onSubmit,
    taskTypes,
    TaskAddWizardProps,
    ...innerProps
  } = useComponentProps(props, 'TaskAddDialog');

  const innerRef = useRef(null);
  const wizardRef = useRef(null);
  const [internalState, setInternalState] = useState({
    error: null,
    isPrevEnabled: false,
    isNextEnabled: false
  });

  useImperativeHandle(ref, () => innerRef.current);

  const dialogControl = useDialogControl();

  const backButtonAction = useMemo(() => ({
    label: 'Back',
    icon: ChevronLeft,
    ButtonProps: {
      color: 'primary',
      variant: 'text'
    },
    auth: internalState.isSubmitting ? utils.createAuth({attribute: 'system.null'}) : null,
    onClick: (e) => {
      wizardRef.current?.goToPrevStep?.();
      e.preventDefault();
    }
  }), [internalState.isSubmitting]);

  const nextButtonAction = useMemo(() => {
    const showNext = (internalState.isNextEnabled || !internalState.isSubmitEnabled);

    return {
      label: showNext ? 'Next' : 'Add task',
      icon: showNext ? ChevronRight : Add,
      iconPosition: showNext ? 'end' : 'start',
      ButtonProps: {
        color: showNext ? 'primary' : 'success',
      },
      auth: (internalState.isSubmitting || (!internalState.isNextEnabled && !internalState.isSubmitEnabled)) ?
        utils.createAuth({attribute: 'system.null'}) : null,
      onClick: (e) => {
        wizardRef.current?.goToNextStep?.();
        e.preventDefault();
      }
    }
  }, [internalState.isNextEnabled, internalState.isSubmitEnabled, internalState.isSubmitting]);

  const handleChange = (isPrevEnabled, isNextEnabled, isSubmitEnabled, isSubmitting) => {
    setInternalState(utils.updater({
      isPrevEnabled,
      isNextEnabled,
      isSubmitEnabled,
      isSubmitting
    }, true));
  }

  const handleSubmit = (task, actions, onSuccess, onError) => {
    setInternalState(utils.updater({isSubmitting: false}, true));

    utils.asPromise(onSubmit)(task)
      .then(() => {
        onSuccess?.();
        onClose?.(null, 'saveButtonClick');
      })
      .catch(() => {
        onError?.();
        setInternalState(utils.updater({
          error: 'Saving task failed'
        }, true));
      })
      .finally(() => {
        setInternalState(utils.updater({isSubmitting: false}, true));
      });
  };

  const handleClose = (e, reason) => {
    if (!wizardRef.current?.isDirty()) {
      onClose?.(e, reason);
    } else if (['escapeKeyDown', 'closeButtonClick', 'cancelButtonClick'].includes(reason)) {
      dialogControl.show(<ConfirmDialog question="Are you sure you want to exit this dialog?"
                                        explanation="The dialog will close and your changes will be lost"
                                        onConfirm={() => onClose?.(e, reason)}/>, true);
    }
  };

  const handleError = (error, validation) => {
    setInternalState(utils.updater({
      error: (error && !validation) ? error : null
    }, true));
  }

  const renderButtons = () => {
    return <React.Fragment>
      {internalState.isPrevEnabled ? <ActionButton key="back"
                                                   showInactive={true}
                                                   action={backButtonAction}/> : null}
      <ActionButton key="next"
                    showInactive={true}
                    action={nextButtonAction}/>
    </React.Fragment>
  }

  const renderInfo = () => {
    if (internalState.error) {
      return <Span color="error">{internalState.error}</Span>;
    }
  }

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <StyledTaskAddDialog ref={innerRef} {...innerProps}
                              size="large"
                              onClose={handleClose}>
    <DialogHeader className="TaskAddDialog-header"
                  title={title ?? 'Add task'}
                  subtitle={subtitle}/>
    <DialogContent className="TaskAddDialog-content">
      <TaskAddWizard ref={wizardRef}
                     variant="standard"
                     className="TaskAddDialog-wizard"
                     taskTypes={taskTypes}
                     showProgressBar={true}
                     onError={handleError}
                     onSubmit={handleSubmit}
                     onChange={handleChange}
                     {...TaskAddWizardProps} />
    </DialogContent>
    <DialogFooter className="TaskAddDialog-footer"
                  variant="split"
                  info={renderInfo()}
                  buttons={renderButtons()} />
  </StyledTaskAddDialog>
});

TaskAddDialog.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  count: PropTypes.number,
  title: PropTypes.any,
  subtitle: PropTypes.any,
  taskTypes: PropTypes.array,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  TaskAddWizardProps: PropTypes.object
};

export default TaskAddDialog;
